import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { DispatchStatePhase } from 'src/app/core/interfaces/dispatchStatePhase';
import { DateManager } from 'src/app/core/managers/date.manager';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { ChangeDispatchStateDialogComponent } from './change-dispatch-state-dialog/change-dispatch-state-dialog.component';
import { PanelAssignmentService } from './panel-assignment.service';
import { CargoStateEnum, CargoStateDict } from 'src/app/core/enums/cargoState.enum';
import { CargoDetailService } from '../cargo-detail/cargo-detail.service';
import { Utils } from 'src/app/core/resources/utils';
import { DispatchStateFingerprintHistory } from 'src/app/core/interfaces/dispatchStateFingerprintHistory';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Destination } from 'src/app/core/interfaces/destination';
import { Subscription } from 'rxjs';
import { Company } from 'src/app/core/interfaces/company';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Fingerprint } from 'src/app/core/interfaces/fingerprint';
import { AssignVehicleCargoComponent } from '../assign-vehicle-cargo/assign-vehicle-cargo.component';
import { AuthService } from 'src/app/core/services/authentication.service';
import { environment } from 'src/environments/environment';
import { VehiclesService } from '../../administration/vehicles/list-vehicles.service';
import { CargoItemService } from '../cargo-item/cargo-item.service';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ReceivableService } from '../../administration/receivable/receivable.service';
import { Titles } from 'src/app/core/resources/titles';
import { ConfirmDriver } from 'src/app/core/interfaces/confirmDriver';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-panel-assignment',
  templateUrl: './panel-assignment.component.html',
  styleUrls: ['./panel-assignment.component.scss']
})
export class PanelAssignmentComponent implements OnInit {
  public Permission = Permission;
  public filters: FormGroup = new FormGroup({
    consecutive: new FormControl(''),
    stateDispatch: new FormControl(''),
    dateLoad: new FormControl(null),
    endDate: new FormControl(null),
    confirmedDriver: new FormControl(false),
    company: new FormControl(null),
    nit: new FormControl(''),
    exclusive: new FormControl(false),
    origin: new FormControl(''),
    pageKey: new FormControl(1),
    pageSize: new FormControl(10),
    states: new FormControl([CargoStateEnum.REQUEST, CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED])
  });
  public readonly pageSizeOptions = [10, 20, 30];
  public companyOptions: OptionsAutocomplete = { title: 'Cliente', appearance: 'outline', showXMark: true, optionAll: true };
  public originOptions: OptionsAutocomplete = { title: 'Origen', appearance: 'outline', showXMark: true };
  public originControl: FormControl = new FormControl();
  public validateCityOrigin: string = '';
  public availableDispatchStates: DispatchStatePhase[] = [];
  public displayedRows: Cargo[] = [];
  public cargoCount: number = 0;
  private subscription: Subscription = new Subscription();
  private confirmCargo: Cargo = null;
  private vehicle: Vehicle = null;
  private lastInspection: any = null;
  private pendingReceivables: string = '';

  constructor(
    private readonly panelAssignmentService: PanelAssignmentService,
    private readonly dialog: MatDialog,
    private readonly snackBarService: SnackBarService,
    private readonly spinner: NgxSpinnerService,
    private readonly cargoDetailService: CargoDetailService,
    public readonly utils: Utils,
    private readonly permissionRole: PermissionRole,
    private readonly authService: AuthService,
    private readonly vehiclesService: VehiclesService,
    private readonly cargoItemService: CargoItemService,
    private readonly cargoManager: CargoManager,
    private readonly receivableService: ReceivableService,
    public readonly titles: Titles
  ) { }

  async ngOnInit() {
    this.loadDispatchStates();
    await this.loadServices();
    this.subscription.add(this.filters.get('company').valueChanges.subscribe((value: Company) => {
      if (((!value || value === 'clean') && this.filters.get('nit').value) ||
        (value && value.companyId && value.companyId !== this.filters.get('nit').value)) {
        this.filters.get('nit').setValue(value && value.companyId ? value.companyId : '');
        this.filters.get('exclusive').setValue(value && value.companyId ? !!value.exclusive : '');
        this.resetPage();
      }
    }));
    this.subscription.add(this.originControl.valueChanges.subscribe((value: any) => {
      if (!value) return;
      if (value.id) {
        this.validateCityOrigin = 'disable';
        this.filters.get('origin').setValue(value.name);
        this.resetPage();
      } else if (value === 'clean') {
        this.validateCityOrigin = 'enable';
        this.filters.get('origin').setValue('');
        this.resetPage();
      }
    }));
  }

  private async loadDispatchStates(): Promise<void> {
    try {
      const dispatchStates = await this.panelAssignmentService.getDispatchStates().toPromise();
      this.availableDispatchStates = dispatchStates && dispatchStates.length ? dispatchStates : [];
    } catch (error) {
      this.availableDispatchStates = [];
    }
  }

  public async loadServices(): Promise<void> {
    const { dateLoad, endDate, consecutive, company, ...filters } = this.filters.value;
    filters['dateLoad'] = DateManager.dateToString(dateLoad, 'YYYY-MM-DD ZZ');
    filters['endDate'] = DateManager.dateToString(endDate, 'YYYY-MM-DD ZZ');
    filters['consecutive'] = consecutive ? consecutive.trim() : '';
    filters['nit'] = company && company.companyId ? company.companyId : '';
    filters['exclusive'] = company && company.companyId ? !!company.exclusive : '';
    delete filters['company'];
    if (filters['stateDispatch'] === 'pending') {
      filters['assignedDriver'] = true;
      delete filters['stateDispatch'];
    }
    this.spinner.show();
    try {
      const services = await this.panelAssignmentService.getServices(filters).toPromise();
      this.displayedRows = services && services.results && services.results.length ? services.results : [];
      this.cargoCount = services && services.pagination && services.pagination.total ? services.pagination.total : 0;
    } catch (error) {
      this.displayedRows = [];
      this.cargoCount = 0;
    } finally {
      this.spinner.hide();
    }
  }

  public onTabChange(tab: 'unconfirmed' | 'confirmed'): void {
    this.filters.controls.confirmedDriver.setValue(tab === 'confirmed');
    this.filters.controls.states.setValue(tab === 'confirmed'
      ? [CargoStateEnum.ACCEPTED, CargoStateEnum.START_SERVICE, CargoStateEnum.END_SERVICE]
      : [CargoStateEnum.REQUEST, CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED]);
    this.filters.get('stateDispatch').setValue('');
    this.resetPage();
  }

  public cleanFilter(): void {
    this.filters.get('consecutive').setValue('');
    this.filters.get('stateDispatch').setValue('');
    this.filters.get('dateLoad').setValue(null);
    this.filters.get('endDate').setValue(null);
    this.filters.get('company').setValue('', { emitEvent: false });
    this.companyOptions = { title: 'Cliente', appearance: 'outline', showXMark: true, optionAll: true, initialNit: '' };
    this.filters.get('origin').setValue('');
    this.originControl.setValue('', { emitEvent: false });
    this.validateCityOrigin = 'enable';
    this.originOptions = { ...this.originOptions, initialValue: '' };
    this.filters.get('pageKey').setValue(1);
    this.loadServices();
  }

  public previousPage(): void {
    if (this.filters.get('pageKey').value <= 1) return;
    this.filters.get('pageKey').setValue(this.filters.get('pageKey').value - 1);
    this.loadServices();
  }

  public nextPage(): void {
    if (!this.displayedRows.length) return;
    this.filters.get('pageKey').setValue(this.filters.get('pageKey').value + 1);
    this.loadServices();
  }

  public updatePageSize(rawPageSize: string): void {
    this.filters.get('pageSize').setValue(Number(rawPageSize));
    this.resetPage();
  }

  public resetPage(): void {
    this.filters.get('pageKey').setValue(1);
    this.loadServices();
  }

  private async getCargo(consecutive: number): Promise<Cargo> {
    try {
      const cargo = await this.cargoDetailService.detailCargoByConsecutive(consecutive.toString()).toPromise();
      if (!cargo || !cargo.id) return null;
      return cargo;
    } catch (error) {
      return null;
    }
  }

  //Acceso a las acciones
  public canExecuteActions(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    if (!item) return { show: false };
    if (!this.isTransportService(item)) return { show: false };
    if (![CargoStateEnum.REQUEST, CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED].includes(item.state as CargoStateEnum))
      return { show: false };
    return { show: true, can: true, message: '' };
  }
  public showActions(item: Cargo): boolean {
    const actions = [
      this.canChangeDispatchState(item),
      this.canEditDispatchObservation(item),
      this.canViewHistoryDispatchStateChanges(item),
      this.canOpenApplications(item),
      this.canAssignVehicle(item),
      this.canConfirmVehicle(item),
      this.canDisassociateVehicle(item)
    ];
    return actions.some(action => action.show);
  }
  public canChangeDispatchState(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    const permission = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.changeDispatchState);
    if (!permission) return { show: true, can: false, message: 'No tienes permisos para cambiar el estado del despacho' };
    if (![CargoStateEnum.REQUEST, CargoStateEnum.CREATED].includes(item.state as CargoStateEnum))
      return { show: true, can: false, message: 'Solo se puede cambiar el estado del despacho en servicios en solicitudes y programadas sin conductor asignado' };
    if (!this.availableDispatchStates || !this.availableDispatchStates.length)
      return { show: true, can: false, message: 'No hay estados de despacho disponibles' };
    if (item.assignedDriver) return { show: true, can: false, message: 'El servicio ya tiene un conductor asignado' };
    return { show: true, can: true, message: '' };
  }
  public canEditDispatchObservation(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    const permission = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.changeDispatchState);
    if (!permission) return { show: true, can: false, message: 'No tienes permisos para editar las observaciones del despacho' };
    if (![CargoStateEnum.REQUEST, CargoStateEnum.CREATED].includes(item.state as CargoStateEnum))
      return { show: true, can: false, message: 'Solo se puede editar las observaciones del despacho en servicios en solicitudes y programadas sin conductor asignado' };
    if (item.assignedDriver) return { show: true, can: false, message: 'El servicio ya tiene un conductor asignado' };
    const observations = this.utils.getNestedValue(item, 'stateDispatch.observations');
    return { show: !!observations, can: !!observations, message: observations ? '' : 'No hay observaciones para editar' };
  }
  public canViewHistoryDispatchStateChanges(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    const permission = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.viewHistoryDispatchStateChanges);
    if (!permission) return { show: true, can: false, message: 'No tienes permisos para ver el historial de cambios' };
    if (![CargoStateEnum.REQUEST, CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED].includes(item.state as CargoStateEnum))
      return { show: true, can: false, message: 'Solo se puede ver el historial de cambios en servicios en solicitudes y programadas sin conductor asignado' };
    const state = this.utils.getNestedValue(item, 'stateDispatch.state');
    return { show: !!state, can: !!state, message: state ? '' : 'No es posible ver el historial de cambios' };
  }
  public canOpenApplications(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    const permissionSpecialLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.specialLicensePlate);
    const permissionAssignLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.assignLicensePlate);
    if (!permissionSpecialLicensePlate && !permissionAssignLicensePlate) return { show: true, can: false, message: 'No tienes permisos para buscar postulaciones' };
    if (![CargoStateEnum.CREATED, CargoStateEnum.REQUEST].includes(item.state as CargoStateEnum))
      return { show: true, can: false, message: 'Solo se puede buscar postulaciones en servicios en programadas sin conductor asignado o en solicitudes' };
    const isReserved = item && item.zone === 'reserved';
    if (isReserved) return { show: true, can: false, message: 'No se pueden buscar postulaciones en servicios con publicación reservada' };
    return { show: true, can: true, message: '' };
  }
  public canAssignVehicle(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    if (![CargoStateEnum.CREATED].includes(item.state as CargoStateEnum))
      return { show: true, can: false, message: `El servicio se encuentra en estado ${CargoStateDict[item.state as CargoStateEnum]}, no se puede asignar vehículo` };
    if (item.confirmedDriver) return { show: true, can: false, message: 'El servicio ya tiene un conductor asignado y confirmado' };
    if (item.licensePlate || item.assignedDriver) return { show: true, can: false, message: 'El servicio ya tiene un vehículo asignado' };
    const permissionSpecialLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.specialLicensePlate);
    const permissionAssignLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.assignLicensePlate);
    if (!permissionSpecialLicensePlate && !permissionAssignLicensePlate) return { show: false };
    const isReserved = item.zone === 'reserved';
    const numberOfOffers = !!this.utils.getNestedValue(item, 'numberOfOffers');
    if (this.isTeclogiUser && !isReserved && numberOfOffers)
      return { show: true, can: false, message: 'Hay postulaciones disponibles para este servicio, revísalas en "Buscar postulaciones"' };
    return { show: true, can: true, message: '' };
  }
  public canConfirmVehicle(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    if (!item || !this.isTransportService(item)) return { show: false };
    if (!item.driver || !item.licensePlate || item.confirmedDriver) return { show: false };
    if (item.state !== CargoStateEnum.ACCEPTED) return { show: false };
    const isSpecialLicensePlate = item.licensePlate === 'C0N111';
    const isLowRiskLicensePlate = this.utils.getNestedValue(item, 'riskProfile.name') === 'Riesgo bajo';
    const hasPermissionSpecialLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.specialLicensePlate);
    const hasPermissionConfirmLicensePlate = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.confirmLicensePlate);
    const hasPermissionConfirmLicensePlateOfLowRiskCargo = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.confirmLicensePlateOfLowRiskCargo);
    if (isSpecialLicensePlate)
      return hasPermissionSpecialLicensePlate
        ? { show: true, can: true, message: '' }
        : { show: true, can: false, message: 'No tienes permisos para confirmar la placa especial' };
    if (isLowRiskLicensePlate && hasPermissionConfirmLicensePlateOfLowRiskCargo)
      return { show: true, can: true, message: '' };
    if (!hasPermissionConfirmLicensePlate)
      return { show: true, can: false, message: 'No tienes permisos para confirmar el vehículo' };
    return { show: true, can: true, message: '' };
  }
  public canDisassociateVehicle(item: Cargo): { show: boolean, can?: boolean, message?: string } {
    const canExecuteActions = this.canExecuteActions(item);
    if (!canExecuteActions.show || !canExecuteActions.can) return canExecuteActions;
    if (!item || !this.isTransportService(item)) return { show: false };
    if (!item.driver) return { show: false };
    if (![CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED, CargoStateEnum.REQUEST].includes(item.state as CargoStateEnum))
      return { show: false };
    if (item.consecutiveMainCargo) return { show: true, can: false, message: 'No se puede desasociar el vehículo de un servicio consolidado' };
    const permission = this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.cancelCargo);
    if (!permission) return { show: true, can: false, message: 'No tienes permisos para desasociar el vehículo' };
    return { show: true, can: true, message: '' };
  }
  public getVisualNumberOfOffers(item: Cargo): string {
    if (!item || !item.numberOfOffers || item.numberOfOffers <= 0) return null;
    if (item.numberOfOffers >= 100) return '+99';
    return `${item.numberOfOffers}`;
  }

  public getCargoState(item: Cargo): string {
    if (!item) return '-';
    const stateKey = item.state ? item.state : (item.requestState ? item.requestState : '');
    return CargoStateDict[stateKey] || stateKey || '-';
  }
  private get isTeclogiUser(): boolean {
    const company = this.authService && this.authService.getCompany();
    return !!(company && company.companyId === environment.rootNit);
  }
  private async validateCargo(consecutive: number, action: (cargo: Cargo) => { show: boolean, can?: boolean, message?: string })
    : Promise<{ canProceed: boolean, message?: string, cargo?: Cargo }> {
    if (!consecutive) return { canProceed: false, message: 'No se encontró el servicio' };
    this.spinner.show();
    const cargo = await this.getCargo(consecutive);
    this.spinner.hide();
    if (!cargo || !cargo.id) return { canProceed: false, message: ServiceMessages.GENERAL_HTTP_ERROR };
    const can = action(cargo);
    if (!can.can) return { canProceed: false, message: can.message ? can.message : 'No se puede realizar la acción' };
    return { canProceed: true, cargo };
  }

  //Acciones
  public async openApplications(consecutive: number): Promise<void> {
    const { canProceed, message } = await this.validateCargo(consecutive, (cargo) => this.canOpenApplications(cargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    this.utils.openLinkNewTab(`/cargo/applications/${consecutive}`);
  }

  public async openModalAssignVehicle(item: Cargo): Promise<void> {
    const { canProceed, message } = await this.validateCargo(item.consecutive, (cargo) => this.canAssignVehicle(cargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    if (!item || !item.consecutive) return this.snackBarService.openSnackBar('No se encontró el servicio', undefined, 'alert');
    this.spinner.show();
    const cargo = await this.getCargo(item.consecutive);
    this.spinner.hide();
    if (!cargo || !cargo.id) return this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'alert');
    const canAssignVehicle = this.canAssignVehicle(cargo);
    if (!canAssignVehicle.can)
      return this.snackBarService.openSnackBar(canAssignVehicle.message ? canAssignVehicle.message : 'No se puede realizar la acción', undefined, 'alert');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Datos del Vehículo Asignado',
      cargo: cargo,
      type: 'assign'
    };
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    const result: { state?: boolean } = await this.dialog.open(AssignVehicleCargoComponent, dialogConfig).afterClosed().toPromise();
    if (result && result.state) this.loadServices();
  }

  public async openModalConfirmDriver(item: Cargo): Promise<void> {
    const { canProceed, message, cargo } = await this.validateCargo(item.consecutive, (currentCargo) => this.canConfirmVehicle(currentCargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    if (cargo && !cargo.vehicleInspection && item && item.vehicleInspection)
      cargo.vehicleInspection = item.vehicleInspection;
    this.confirmVehicle(cargo);
  }

  public async disassociateVehicle(item: Cargo): Promise<void> {
    const { canProceed, message, cargo } = await this.validateCargo(item.consecutive, (currentCargo) => this.canDisassociateVehicle(currentCargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    const hasGeneratedManifest = cargo.ministry && cargo.manifestAuthorization && cargo.rndcTransmited;
    if (hasGeneratedManifest) {
      this.dialog.open(DialogComponent, {
        data: {
          title: 'No se puede desasociar el vehículo',
          description: 'Este servicio cuenta con un manifiesto generado. Es necesario anular el manifiesto antes de desasociar el vehículo.',
          hideBtnCancel: true,
          iconError: true,
        },
        height: 'auto',
        width: ModalEnum.SMALL_WIDTH,
        maxWidth: ModalEnum.MAX_WIDTH,
        autoFocus: false
      });
      return;
    }
    this.spinner.show();
    this.cargoDetailService.cancelCargo({ cargoId: cargo.id, userId: cargo.driver }).subscribe(
      (data) => {
        this.spinner.hide();
        if (data) {
          this.snackBarService.openSnackBar('Vehículo desasociado correctamente');
          this.loadServices();
        } else {
          this.snackBarService.openSnackBar('Ocurrió un error al realizar el proceso', undefined, 'error');
        }
      },
      () => {
        this.spinner.hide();
        this.snackBarService.openSnackBar('Ocurrió un error al realizar el proceso', undefined, 'error');
      }
    );
  }

  private async confirmVehicle(cargo: Cargo): Promise<void> {
    if (!cargo || !cargo.licensePlate)
      return this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
    this.confirmCargo = cargo;
    this.lastInspection = null;
    this.vehicle = null;
    this.pendingReceivables = '';
    this.spinner.show();
    try {
      this.vehicle = await this.vehiclesService.getVehicleFromOwnCompany(cargo.licensePlate).toPromise();
      if (!this.vehicle) {
        this.spinner.hide();
        return this.snackBarService.openSnackBar('No se encontró el vehículo', undefined, 'error');
      }
      if (cargo.vehicleInspection) {
        try {
          this.lastInspection = await this.cargoItemService.getInspection(cargo.vehicleInspection, 'vehicle').toPromise();
        } catch (error) {
          this.lastInspection = null;
        }
      }
    } catch (error) {
      this.spinner.hide();
      return this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
    }
    this.spinner.hide();
    this.handleInspectionAndConfirm();
  }

  private handleInspectionAndConfirm(): void {
    const assignment = 'vehículo';
    if (!this.lastInspection || !this.lastInspection.id) {
      const whoDocument = this.confirmCargo.assignedVehicleFingerPrint
        ? this.confirmCargo.assignedVehicleFingerPrint.userId
        : this.confirmCargo.numberDocumentCreatorLoad;
      const userSession = this.authService && this.authService.getUserSession();
      const currentUserDocument = userSession && userSession.information ? userSession.information.document : null;
      if (currentUserDocument && whoDocument === currentUserDocument)
        return this.createInspection();
      const who = this.confirmCargo.assignedVehicleFingerPrint
        ? this.confirmCargo.assignedVehicleFingerPrint.userName
        : `el Operador que asignó el ${assignment}`;
      this.dialog.open(DialogComponent, {
        data: {
          title: `No se encontró registrada una inspección preoperacional del ${assignment}. 
            Debes comunicarte con ${who} para solicitar el registro de la inspección.`,
          hideBtnCancel: true,
        },
        maxHeight: ModalEnum.MAX_HEIGHT,
        width: ModalEnum.SMALL_WIDTH,
        maxWidth: ModalEnum.MAX_WIDTH,
        autoFocus: false
      });
      return;
    }
    if (!this.utils.isDefined(this.lastInspection.approval) || !this.lastInspection.approval) {
      if (this.canApprovePreoperationalInspection(this.lastInspection)) {
        this.openInspectionApprovalAndContinue();
        return;
      }
      if (!this.utils.isDefined(this.lastInspection.approval))
        return this.snackBarService.openSnackBar(`Aún no se ha aprobado la Inspección preoperacional del ${assignment}`, undefined, 'alert');
      return this.snackBarService.openSnackBar(`La Inspección preoperacional del ${assignment} está rechazada`, undefined, 'error');
    }
    this.validateReceivables();
  }

  private canApprovePreoperationalInspection(inspection): boolean {
    if (!inspection || !inspection.id) return false;
    if (!this.utils.isDefined(inspection.approval)) return true;
    if (!inspection.approval)
      return this.permissionRole.hasPermission(this.Permission.cargo.module, this.Permission.cargo.approveRejectedInspection);
    return false;
  }

  private async openInspectionApprovalAndContinue(): Promise<void> {
    const userSession = this.authService.getUserSession();
    await this.cargoManager.openExistingVehicleInspection({
      cargo: this.confirmCargo,
      inspection: this.lastInspection,
      licensePlate: this.confirmCargo.licensePlate,
      user: (userSession && userSession.information) || null
    });
    if (this.confirmCargo && this.confirmCargo.vehicleInspection) {
      try {
        this.lastInspection = await this.cargoItemService.getInspection(this.confirmCargo.vehicleInspection, 'vehicle').toPromise();
      } catch (error) { }
    }
    if (this.lastInspection && this.lastInspection.approval) {
      this.validateReceivables();
      return;
    }
  }

  private createInspection(): void {
    this.cargoManager.checkInspection(this.confirmCargo, this.authService.getUserSession().information, this.confirmCargo.licensePlate)
      .then((inspectionId) => {
        if (inspectionId) {
          this.confirmCargo.vehicleInspection = inspectionId as any;
          this.confirmVehicle(this.confirmCargo);
        }
      }).catch(() => { });
  }

  private validateReceivables(): void {
    if (this.vehicle && this.vehicle.bankAccountBalance && this.vehicle.bankAccountBalance.document) {
      this.spinner.show();
      this.receivableService.getReceivableList(null, null, this.vehicle.bankAccountBalance.document).subscribe({
        next: (data) => {
          if (data && data.some(receivable => receivable.state && receivable.state.description && receivable.state.description === 'pending')) {
            this.pendingReceivables = `El tenedor de este vehiculo ( ${this.vehicle.bankAccountBalance.name} ) tiene carteras pendientes. ¿Está seguro que desea confirmar la placa ${this.confirmCargo.licensePlate} como vehiculo para el servicio ${this.confirmCargo.consecutive}?`;
          } else {
            this.pendingReceivables = '';
          }
        },
        error: () => {
          this.spinner.hide();
          this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
        },
        complete: () => {
          this.spinner.hide();
          this.dialogConfirmDriver();
        }
      });
      return;
    }
    this.pendingReceivables = '';
    this.dialogConfirmDriver();
  }

  private dialogConfirmDriver(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: this.titles.titleConfirmDriver,
      subtitle: this.pendingReceivables ? this.pendingReceivables : '',
      licen: this.pendingReceivables ? '' : this.confirmCargo.licensePlate,
      idCargo: this.pendingReceivables ? '' : this.confirmCargo.consecutive,
    };
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.width = this.pendingReceivables ? ModalEnum.LARGE_WIDTH : ModalEnum.MEDIUM_WIDTH;
    this.dialog.open(DialogComponent, dialogConfig).afterClosed().subscribe((result) => {
      if (!result || !result.state) return;
      const userSession = this.authService.getUserSession();
      const data: ConfirmDriver = {
        cargoId: this.confirmCargo.id,
        fingerprint: {
          userId: userSession.information.document,
          userName: userSession.information.name,
        },
      };
      this.confirmDriver(data, result.refuse);
    });
  }

  private confirmDriver(data: ConfirmDriver, approval: string): void {
    this.spinner.show();
    this.cargoItemService.confirmDrive(data, approval).subscribe(
      (success: Cargo) => {
        this.spinner.hide();
        if (success && !this.utils.isEmpty(success.message)) {
          this.dialog.open(DialogComponent, {
            data: {
              title: success.message,
              iconError: true,
              hideBtnConfirm: true,
            },
            maxHeight: ModalEnum.MAX_HEIGHT,
            width: ModalEnum.EXTRA_SMALL_WIDTH,
            maxWidth: ModalEnum.MAX_WIDTH,
            autoFocus: false
          });
        } else {
          this.snackBarService.openSnackBar('Confirmación realizada correctamente');
        }
        this.loadServices();
      },
      (error: HttpErrorResponse) => {
        this.spinner.hide();
        if (error && error.error && error.error.message)
          return this.snackBarService.openSnackBar(error.error.message, undefined, 'alert');
        this.snackBarService.openSnackBar('Ocurrió un error al realizar la confirmación', undefined, 'error');
      }
    );
  }

  public async openChangeDispatchStateDialog(item: Cargo): Promise<void> {
    const { canProceed, message } = await this.validateCargo(item.consecutive, (cargo) => this.canChangeDispatchState(cargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = ModalEnum.SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      availableStates: this.availableDispatchStates,
      currentState: item.stateDispatch ? item.stateDispatch.state : '',
      currentObservations: item.stateDispatch ? item.stateDispatch.observations : '',
      cargoId: item.id,
      operation: 'changeState',
    };
    const result: { state: boolean } = await this.dialog.open(ChangeDispatchStateDialogComponent, dialogConfig).afterClosed().toPromise();
    if (!result || !result.state) return;
    this.loadServices();
  }

  public async openEditDispatchObservationDialog(item: Cargo): Promise<void> {
    const { canProceed, message } = await this.validateCargo(item.consecutive, (cargo) => this.canEditDispatchObservation(cargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = ModalEnum.SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      currentObservation: item.stateDispatch ? item.stateDispatch.observations : '',
      cargoId: item.id,
      operation: 'changeObservations',
    };

    const result: { state: boolean } = await this.dialog.open(ChangeDispatchStateDialogComponent, dialogConfig).afterClosed().toPromise();
    if (!result || !result.state) return;
    this.loadServices();
  }

  private getDispatchStateHistoryDescription(history: DispatchStateFingerprintHistory[]): string {
    let descriptionHTML = '<small class="my-4">A continuación se muestra el historial de cambios del estado del despacho, ordenados desde el más antiguo hasta el más reciente.</small>';
    let indexOfChanges = 1;
    [...history].forEach((historyItem) => {
      const newStateDispatch = this.utils.getNestedValue(historyItem, 'data.newStateDispatch') as string;
      const observations = this.utils.getNestedValue(historyItem, 'data.observations') as string;
      const previousObservations = this.utils.getNestedValue(historyItem, 'data.previousObservations') as string;
      const previousStateDispatch = this.utils.getNestedValue(historyItem, 'data.previousStateDispatch') as string;
      const hasStateChanges = !previousStateDispatch || (previousStateDispatch && previousStateDispatch !== newStateDispatch);
      const hasObservationChanges = !previousObservations || (previousObservations && previousObservations !== observations);
      if (!hasObservationChanges && !hasStateChanges) return;
      const fingerprintDate = this.utils.getNestedValue(historyItem, 'fingerprint.date') as string;
      const responsibleName = this.utils.getNestedValue(historyItem, 'fingerprint.userName') as string;
      const responsibleDocument = this.utils.getNestedValue(historyItem, 'fingerprint.userId') as string;
      const formattedDate = fingerprintDate ? DateManager.formatDate(fingerprintDate, 'YYYY-MM-DD HH:mm ZZ', 'DD/MM/YYYY hh:mm a') : '';
      descriptionHTML += `
        <div class="mt-2">
          <div class="alert alert-info p-2 px-4 mb-2 d-flex justify-content-between flex-wrap">
            <div class="text-bold">${indexOfChanges}° Cambio</div>
            <div>${formattedDate}</div>
          </div>
          <div class="pl-3 mb-2 d-flex flex-column">
            ${newStateDispatch && hasStateChanges ? `<div class="col-12"><strong class="text-bold">Nuevo estado:</strong> ${newStateDispatch}</div>` : ''}
            ${previousStateDispatch && hasStateChanges ? `<div class="col-12"><strong class="text-bold">Estado anterior:</strong> ${previousStateDispatch}</div>` : ''}
            ${observations && hasObservationChanges ? `<div class="col-12"><strong class="text-bold">Observación:</strong> ${observations}</div>` : ''}
            ${previousObservations && hasObservationChanges ? `<div class="col-12"><strong class="text-bold">Observación anterior:</strong> ${previousObservations}</div>` : ''}
            ${responsibleName ? `<div class="col-12"><strong class="text-bold">Responsable:</strong> ${responsibleName}${responsibleDocument ? ` (${responsibleDocument})` : ''}</div>` : ''}
          </div>
        </div>
      `;
      indexOfChanges++;
    });
    return descriptionHTML + '<div class="mb-5"></div>';
  }

  public async openHistoryDispatchStateChanges(item: Cargo): Promise<void> {
    const { canProceed, message } = await this.validateCargo(item.consecutive, (cargo) => this.canViewHistoryDispatchStateChanges(cargo));
    if (!canProceed) return this.snackBarService.openSnackBar(message, undefined, 'alert');
    const cargoId = this.utils.getNestedValue(item, 'id') as string;
    if (!cargoId) return;
    let history: DispatchStateFingerprintHistory[] = [];
    this.spinner.show();
    try {
      history = await this.cargoDetailService.getFingerPrintHistory(cargoId, 'changeStateDispatch').toPromise();
    } catch (error) { }
    this.spinner.hide();
    if (!history || !history.length)
      return this.snackBarService.openSnackBar('No se encontraron cambios registrados para el estado del despacho', undefined, 'info');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Historial de cambios',
      descriptionHTML: this.getDispatchStateHistoryDescription(history),
      hideBtnConfirm: true
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    this.dialog.open(DialogComponent, dialogConfig);
  }

  public getDispatchState(item: Cargo): string {
    if (item && item.assignedDriver) return 'Pendiente de confirmación';
    const dispatchState = this.utils.getNestedValue(item, 'stateDispatch.state');
    if (dispatchState) return dispatchState;
    const state = this.utils.getNestedValue(item, 'state') as CargoStateEnum;
    if (state === CargoStateEnum.REQUEST) return 'Pendiente de revisión';
    return 'Programado';
  }
  public getLastDestination(item: Cargo): Destination {
    const destinations = this.utils.getNestedValue(item, 'cargoFeature.uploadDownload.destination');
    const lastDestination = destinations && destinations.length ? destinations[destinations.length - 1] : null;
    return lastDestination;
  }
  public isTransportService(item: Cargo): boolean {
    return this.utils.isTransportService(item);
  }
  public getAssignmentFingerprint(item: Cargo): string {
    if (!item || !item.assignedVehicleFingerPrint || !item.licensePlate) return null;
    const assignmentFingerprint: Fingerprint = this.utils.getNestedValue(item, 'assignedVehicleFingerPrint');
    const date = assignmentFingerprint.date ? DateManager.formatDate(assignmentFingerprint.date, 'YYYY-MM-DD HH:mm ZZ', 'DD/MM/YYYY HH:mm') : '-';
    const fingerprintDescription = `Asignado por: ${assignmentFingerprint.userName} (${assignmentFingerprint.userId}) - Fecha: ${date}`;
    return fingerprintDescription;
  }

  public get tableColumns(): string[] {
    const confirmedDriver = !!this.filters.value.confirmedDriver;
    if (confirmedDriver) {
      return ['id', 'contractor', 'origin', 'destination', 'dateLoad', 'dateDownload', 'vehicle', 'bodyWorkType', 'licensePlate', 'state', 'actions'];
    }
    const columns = ['id', 'contractor', 'origin', 'destination', 'dateLoad', 'dateDownload', 'vehicle', 'bodyWorkType'];
    if (this.availableDispatchStates && this.availableDispatchStates.length) {
      columns.push('dispatchState');
      if (this.displayedRows && this.displayedRows.length &&
        this.displayedRows.some(row => !!(row.stateDispatch && row.stateDispatch.observations))
      ) columns.push('observations');
    }
    columns.push('state');
    if (this.displayedRows && this.displayedRows.length && this.displayedRows.some(row => !!row.licensePlate))
      columns.push('licensePlate');
    columns.push('actions');
    return columns;
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
